import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import { appDate, appDateTime } from '../../../../utils/appDateFormat';

const ApprovalWorkflow = ({ requests, userRole, onApproveRequest, onRejectRequest, isProcessing: isProcessingProp }) => {
  const { t } = useTranslation();
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const [comment, setComment] = useState('');
  const [isProcessingLocal, setIsProcessingLocal] = useState(false);
  const [actionError, setActionError] = useState(null);
  const isProcessing = isProcessingProp ?? isProcessingLocal;

  const extractApiErrorMessage = (e, fallback) => {
    if (!e) return fallback;
    if (typeof e === 'string') return e;

    const body = e?.response?.data || e?.data || e;
    const errors = body?.errors;
    if (Array.isArray(errors) && errors.length > 0) {
      const descriptions = errors
        .map((x) => x?.description || x?.message || x?.code)
        .filter(Boolean)
        .map((x) => String(x));
      if (descriptions.length) return descriptions.join('\n');
    }

    const msg = body?.message || e?.message;
    if (msg) return String(msg);

    const status = e?.status || e?.response?.status;
    const statusText = e?.statusText || e?.response?.statusText;
    if (status) return statusText ? `${status} ${statusText}` : String(status);

    return fallback;
  };

  const getRequesterName = (request) => {
    const submitted = request?.workflow?.find?.(s => s?.stage === 'submitted');
    const name = submitted?.actor;
    const label = name && String(name).trim().length > 0 ? String(name).trim() : null;
    if (!label) return null;
    if (label.toLowerCase() === 'employee') return null;
    return label;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-border';
    }
  };

  const getTypeLabel = (type) => {
    const types = {
      'missing-clockout': t('Missing Clock Out'),
      'incorrect-time': t('Incorrect Time'),
      'missing-entry': t('Missing Entry'),
      'break-time': t('Break Time Correction')
    };
    return types?.[type] || type;
  };

  const formatDate = (dateString) => {
    return appDate(new Date(dateString));
  };

  const formatDateTime = (dateString) => {
    return appDateTime(new Date(dateString));
  };

  const getCurrentApprovalStage = (request) => {
    const workflow = request?.workflow || [];
    const pendingStage = workflow?.find(stage => stage?.status === 'pending');
    return pendingStage?.stage || 'unknown';
  };

  const canUserApprove = (request) => {
    const currentStage = getCurrentApprovalStage(request);
    
    if (userRole === 'manager' && currentStage === 'manager_review') {
      return true;
    }
    
    if (userRole === 'hr' && (currentStage === 'hr_approval' || currentStage === 'manager_review')) {
      return true;
    }
    
    return false;
  };

  const handleApprovalAction = async (request, action) => {
    if (!comment?.trim() && action === 'reject') {
      setActionError(t('Please provide a reason for rejection'));
      return;
    }

    setActionError(null);
    setIsProcessingLocal(true);

    try {
      if (action === 'approve') {
        await onApproveRequest?.(request, comment);
      } else {
        await onRejectRequest?.(request, comment);
      }
      
      // Reset form
      setSelectedRequest(null);
      setActionType(null);
      setComment('');
    } catch (error) {
      const fallback = action === 'approve' ? t('Failed to approve request.') : t('Failed to reject request.');
      setActionError(extractApiErrorMessage(error, fallback));
    } finally {
      setIsProcessingLocal(false);
    }
  };

  const openApprovalModal = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setComment('');
    setActionError(null);
  };

  const closeModal = () => {
    setSelectedRequest(null);
    setActionType(null);
    setComment('');
    setActionError(null);
  };

  const getPriorityLevel = (request) => {
    const submittedDate = new Date(request?.submittedDate);
    const daysSinceSubmission = Math.floor((Date.now() - submittedDate) / (1000 * 60 * 60 * 24));
    
    if (daysSinceSubmission >= 3) return { level: 'high', label: t('High Priority'), color: 'text-red-600' };
    if (daysSinceSubmission >= 1) return { level: 'medium', label: t('Medium Priority'), color: 'text-orange-600' };
    return { level: 'low', label: t('Normal Priority'), color: 'text-green-600' };
  };

  const RequestCard = ({ request }) => {
    const priority = getPriorityLevel(request);
    const canApprove = canUserApprove(request);
    const currentStage = getCurrentApprovalStage(request);
    const requesterName = getRequesterName(request);

    return (
      <div className="relative isolate bg-card border border-border rounded-xl p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-lg flex items-center justify-center">
              <Icon name="FileEdit" size={20} className="text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">{request?.id}</h3>
              <p className="text-sm text-muted-foreground">{getTypeLabel(request?.type)}</p>
            </div>
          </div>
          
          <div className="text-right space-y-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(request?.status)}`}>
              {request?.status?.toUpperCase()}
            </span>
            <div className={`text-xs font-medium ${priority?.color}`}>
              {priority?.label}
            </div>
          </div>
        </div>

        {/* Request Details */}
        <div className="space-y-3">
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center space-x-1">
              <Icon name="Calendar" size={14} className="text-muted-foreground" />
              <span className="text-muted-foreground">{t('Date:', { nsSeparator: false })}</span>
              <span className="font-medium text-foreground">{formatDate(request?.date)}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Clock" size={14} className="text-muted-foreground" />
              <span className="text-muted-foreground">{t('Submitted:', { nsSeparator: false })}</span>
              <span className="font-medium text-foreground">{formatDate(request?.submittedDate)}</span>
            </div>
          </div>

          {requesterName && (
            <div className="flex items-center space-x-2 text-sm">
              <Icon name="User" size={14} className="text-muted-foreground" />
              <span className="text-muted-foreground">{t('Employee:', { nsSeparator: false })}</span>
              <span className="font-medium text-foreground">{requesterName}</span>
            </div>
          )}

          {/* Time Changes */}
          {(request?.requestedCheckIn || request?.requestedCheckOut) && (
            <div className="bg-muted/30 rounded-lg p-3 space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase">{t('Requested Changes')}</p>
              {request?.requestedCheckIn && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t('Check In:', { nsSeparator: false })}</span>
                  <span className="font-mono">
                    {request?.currentCheckIn ? (
                      <>
                        <span className="text-red-600 line-through">{request?.currentCheckIn}</span>
                        <span className="mx-2">→</span>
                        <span className="text-green-600">{request?.requestedCheckIn}</span>
                      </>
                    ) : (
                      <span className="text-green-600">{request?.requestedCheckIn}</span>
                    )}
                  </span>
                </div>
              )}
              {request?.requestedCheckOut && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t('Check Out:', { nsSeparator: false })}</span>
                  <span className="font-mono">
                    {request?.currentCheckOut ? (
                      <>
                        <span className="text-red-600 line-through">{request?.currentCheckOut}</span>
                        <span className="mx-2">→</span>
                        <span className="text-green-600">{request?.requestedCheckOut}</span>
                      </>
                    ) : (
                      <span className="text-green-600">{request?.requestedCheckOut}</span>
                    )}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Reason */}
          <div>
            <p className="text-sm font-medium text-foreground mb-1">{t('Reason:', { nsSeparator: false })}</p>
            <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">
              {request?.reason}
            </p>
          </div>

          {/* Attachments */}
          {request?.attachments?.length > 0 && (
            <div>
              <p className="text-sm font-medium text-foreground mb-2">{t('Attachments:', { nsSeparator: false })}</p>
              <div className="space-y-2">
                {request?.attachments?.map((attachment, index) => (
                  <div key={index} className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="Paperclip" size={14} />
                    <span>{attachment}</span>
                    <button type="button" className="text-blue-600 hover:text-blue-800 text-xs">{t('View')}</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Current Stage */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm font-medium text-blue-900 mb-1">{t('Current Stage:', { nsSeparator: false })}</p>
            <p className="text-sm text-blue-700 capitalize">
              {currentStage?.replace('_', ' ')} 
              {canApprove && <span className="font-semibold"> {t('(Action Required)')}</span>}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        {canApprove && (
          <div className="flex space-x-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={(e) => {
                e?.preventDefault?.();
                e?.stopPropagation?.();
                openApprovalModal(request, 'approve');
              }}
              className="relative z-10 pointer-events-auto flex-1 flex items-center justify-center space-x-2 py-3 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Icon name="Check" size={16} />
              <span>{t('Approve')}</span>
            </button>
            <button
              type="button"
              onClick={(e) => {
                e?.preventDefault?.();
                e?.stopPropagation?.();
                openApprovalModal(request, 'reject');
              }}
              className="relative z-10 pointer-events-auto flex-1 flex items-center justify-center space-x-2 py-3 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Icon name="X" size={16} />
              <span>{t('Reject')}</span>
            </button>
          </div>
        )}

        {!canApprove && (
          <div className="pt-4 border-t border-border">
            <p className="text-sm text-muted-foreground text-center">
              {t('Waiting for')} {currentStage?.replace('_', ' ')?.replace('review', 'approval')}
            </p>
          </div>
        )}
      </div>
    );
  };

  if (!requests?.length) {
    return (
      <div className="text-center py-12 bg-card border border-border rounded-xl">
        <Icon name="CheckSquare" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">{t('No Pending Approvals')}</h3>
        <p className="text-muted-foreground">
          {t('All correction requests have been processed.')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-orange-600">
            {requests?.filter(req => getPriorityLevel(req)?.level === 'high')?.length || 0}
          </div>
          <div className="text-sm text-orange-700">{t('High Priority')}</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">
            {requests?.filter(req => getCurrentApprovalStage(req) === 'manager_review')?.length || 0}
          </div>
          <div className="text-sm text-blue-700">{t('Manager Review')}</div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">
            {requests?.filter(req => getCurrentApprovalStage(req) === 'hr_approval')?.length || 0}
          </div>
          <div className="text-sm text-purple-700">{t('HR Approval')}</div>
        </div>
      </div>

      {/* Requests Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {requests?.map(request => (
          <RequestCard key={request?.id} request={request} />
        ))}
      </div>

      {/* Approval Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">
                {actionType === 'approve' ? t('Approve Request') : t('Reject Request')}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {actionError ? (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3" style={{ whiteSpace: 'pre-line' }}>
                  {actionError}
                </div>
              ) : null}

              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-sm font-medium text-foreground">{t('Request ID:', { nsSeparator: false })} {selectedRequest?.id}</p>
                <p className="text-sm text-muted-foreground">{t('Type:', { nsSeparator: false })} {getTypeLabel(selectedRequest?.type)}</p>
                <p className="text-sm text-muted-foreground">{t('Date:', { nsSeparator: false })} {formatDate(selectedRequest?.date)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {actionType === 'approve' ? t('Approval Comment (Optional)') : t('Rejection Reason *')}
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e?.target?.value)}
                  rows={3}
                  placeholder={
                    actionType === 'approve'
                      ? t('Add any comments about this approval...')
                      : t('Explain why this request is being rejected...')
                  }
                  className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors resize-none"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-2 px-4 text-sm font-medium text-muted-foreground bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                  disabled={isProcessing}
                >
                  {t('Cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => handleApprovalAction(selectedRequest, actionType)}
                  disabled={isProcessing || (actionType === 'reject' && !comment?.trim())}
                  className={`flex-1 py-2 px-4 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 ${
                    actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {isProcessing && <Icon name="Loader2" size={16} className="animate-spin" />}
                  <span>
                    {isProcessing 
                      ? (actionType === 'approve' ? t('Approving...') : t('Rejecting...'))
                      : (actionType === 'approve' ? t('Approve Request') : t('Reject Request'))
                    }
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalWorkflow;